// Constants
import { ClientAppId, getValidOriginsForHost } from "@ms/uno-constants/lib/local/AppConstants";
import { SettingsConstants } from "@ms/uno-constants/lib/local/SettingsConstants";
import { TelemetryPlatform } from "@ms/uno-constants/lib/local/TelemetryConstants";
import { WebQueryParams, WebUrlSegments } from "@ms/uno-constants/lib/local/WebConstants";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Models
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Meta hub sdk
import * as metaOSAppSDK from "@microsoft/teams-js-uno";
import { HostClientType } from "@microsoft/teams-js-uno";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { generateLogStringFromError } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { generateDeepLinkFromRoute, generateRouteFromDeepLink } from "@ms/uno-routing";
import { MsalAuthRouter } from "@ms/uno-utilities/lib/local/auth/msal/MsalAuthRouter";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { HtmlSettingsReader } from "@ms/uno-utilities/lib/local/HtmlSettingsReader";
import { PageTimingKey, setPageTiming } from "@ms/uno-utilities/lib/local/PASUtilities";
import JwtDecode from "jwt-decode";
import { getFileExtension } from "../utils/ContentExtensionUtils";
import { generateRouteFromLegacyDeepLink } from "../utils/LegacyTeamsDeepLinkUtils";
import { tryParseTabRoute } from "../utils/TeamsRouteUtils";
import { collectScriptCacheMetrics } from "../utils/WebPlatformPerformanceMetrics";
/**
 * Class implementing IHostAdaptor interface to provide interaction with Meta OS Hubs platform
 */ export class MetaOSHostAdaptor {
    get rootUrl() {
        if (this._rootUrl === undefined) {
            this._rootUrl = this.computeRootUrl();
        }
        return this._rootUrl;
    }
    /**
     * Sets the loggers to be used by the adaptor
     */ setLoggers(loggers) {
        this.loggers = loggers;
        this.getContextFromMetaOSAppSDK().then((context)=>{
            if (!this.isRingIdPresent()) {
                this.loggers?.traceLogger.logTrace(0x1e19e80e /* tag_4g46o */ , TraceLevel.Warning, `initFromMetaHubsSdkContext - Invalid ringId. [RingId=${context.app?.host?.ringId}]`);
            }
            this.loggers?.traceLogger.logTrace(0x1e19e80d /* tag_4g46n */ , TraceLevel.Info, `locale: ${context.app.locale} osLocaleInfo.regionalFormat: ${context.app.osLocaleInfo?.regionalFormat ?? "null"} osLocaleInfo.longDate: ${context.app.osLocaleInfo?.longDate ?? "null"}`);
        });
    }
    /**
     * Retrieves the current context information from the MetaOS App SDK and maps it to the `IContext` interface.
     *
     * @remarks
     * Host-specific adaptors should extend this class and override this method.
     *
     * @returns A promise that resolves to an `IContext` object containing locale, theme, session, user, tenant, ring, platform, and role information.
     */ async getContext() {
        const metaOSContext = await this.getContextFromMetaOSAppSDK();
        const context = {
            locale: metaOSContext.app.locale,
            theme: this.mapToUnoTheme(metaOSContext.app.theme),
            sessionId: metaOSContext.app.host.sessionId,
            userId: metaOSContext.user?.id ?? null,
            displayName: metaOSContext.user?.displayName ?? null,
            userUpn: metaOSContext.user?.userPrincipalName ?? null,
            tenantId: metaOSContext.user?.tenant?.id ?? null,
            ringId: this.normalizeRingId(metaOSContext.app.host.ringId ?? null),
            platform: this.getTelemetryPlatform(metaOSContext.app.host.clientType),
            isGuestUser: metaOSContext.team?.userRole === metaOSAppSDK.UserTeamRole.Guest,
            isTenantAdmin: metaOSContext.team?.userRole === metaOSAppSDK.UserTeamRole.Admin,
            isBackgroundLoad: metaOSContext.page.isBackgroundLoad ?? false
        };
        return context;
    }
    /**
     * Generates a deep link URL based on the provided route descriptor.
     * The generated URL falls back to the web UI, providing deep links for web app.
     *
     * @remarks
     * Host-specific adaptors should extend this class and override this method.
     *
     * @param route - The route descriptor used to generate the deep link.
     * @returns The generated deep link URL as a string, or `null` if generation fails.
     */ generateDeepLink(route) {
        const deepLink = generateDeepLinkFromRoute(route, this.loggers);
        if (!deepLink) {
            this.loggers?.traceLogger.logTrace(0x1e147740 /* tag_4fh3a */ , TraceLevel.Error, `generateDeepLink - Unable to generate DeepLink from Route`);
            return null;
        }
        let deepLinkUrl = null;
        try {
            // Falling back to webUI, since this is generic adaptor.
            const origin = this.configProvider().settings.serviceConfigurations.plannerWeb.hostname;
            deepLinkUrl = new URL(`${origin}/${WebUrlSegments.WebUI}${deepLink}`);
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e147723 /* tag_4fh29 */ , TraceLevel.Error, `generateDeepLink - Error instantiating URL object`);
            return null;
        }
        if (this.tenantId) {
            deepLinkUrl.searchParams.set(WebQueryParams.TenantId, this.tenantId);
        }
        return deepLinkUrl.toString();
    }
    /**
     * Initializes state from current MetaHubsContext (which can change when load from App Cache)
     * e.g., ChannelTab -> App, or ChannelTab1 -> ChannelTab2
     *
     * @remarks
     * Host-specific adaptors should extend this class and override this method.
     */ async initFromMetaOSAppContext() {
        const metaOSContext = await this.getContextFromMetaOSAppSDK();
        this.ringId = this.normalizeRingId(metaOSContext.app.host.ringId ?? null);
        this.pageId = metaOSContext.page.id;
        this.tenantId = metaOSContext.user?.tenant?.id ?? null;
    }
    /**
     * Ensures that app config is valid for the current MetaHubs context.
     * Checks whether ringId in sessionMetaData matches ringId from metaHubsContext
     *
     * @remarks
     * Host-specific adaptors should extend this class and override this method.
     *
     * @returns validation error message. null if valid.
     */ validateRingId() {
        const { ringId: sessionRingId } = this.configProvider().sessionMetaData;
        if (this.ringId !== null && this.normalizeRingId(this.ringId) === this.normalizeRingId(sessionRingId)) {
            return null;
        }
        return `Invalid ring id`;
    }
    /**
     * Set up Nested App Auth
     * @param loggers
     */ async setupNestedAppAuth(loggers) {
        if (!metaOSAppSDK.nestedAppAuth.isNAAChannelRecommended()) {
            this.loggers?.traceLogger.logTrace(0x1e19e80c /* tag_4g46m */ , TraceLevel.Error, `Nested App Auth is not supported.`);
            this.useNestAppAuth = false;
            return;
        }
        const environment = this.configProvider().sessionMetaData.environment;
        // Initialize Nested Ap Auth
        try {
            const setupCorrelationId = generateGuid();
            loggers.traceLogger.logTrace(0x1e19e80b /* tag_4g46l */ , TraceLevel.Info, `setupNestedAppAuth. [CorrelationId=${setupCorrelationId}]`);
            const msalConfigSettings = {
                clientId: ClientAppId.MicrosoftPlannerClient,
                environment,
                clientName: this.appName,
                correlationId: setupCorrelationId,
                tenantId: this.tenantId || ""
            };
            const options = {
                useCachedPromises: true,
                handleInteractionRequiredOnSilentLogin: true,
                cp1ClientCapability: this.configProvider().flights.EnableCp1ClientCapabilityInTeams
            };
            this.nestedAppAuthHelper = await MsalAuthRouter.createAndInitializeNestedAppAuthHelper(msalConfigSettings, loggers, options, this.configProvider);
            this.useNestAppAuth = true;
        } catch (error) {
            this.loggers?.traceLogger.logTrace(0x1e19e80a /* tag_4g46k */ , TraceLevel.Error, `Failed to set up Nest App Auth: [ErrorMessage=${ErrorUtilities.getMessage(error)}]`);
            this.useNestAppAuth = false;
        }
    }
    /**
     * Returns valid message origins to pass to the SDK's initialize method.
     * Uses the client type and environment from session metadata to look up
     * host-specific origins. Returns undefined for hosts/environments without
     * custom origins, which causes the SDK to use its built-in defaults.
     */ getValidMessageOrigins() {
        const reader = new HtmlSettingsReader("planner");
        const sessionMetaData = reader.getSetting(SettingsConstants.SessionMetaData);
        return getValidOriginsForHost(sessionMetaData?.appMetadata?.clientType, sessionMetaData?.environment);
    }
    /**
     * Initializes the SDK
     */ async initializeSdk() {
        await metaOSAppSDK.app.initialize(this.getValidMessageOrigins());
        // Get the timestamp of the user's click on the app to include in the extra data for the InitializedmetaOSAppSDK marker.
        const metaHubsSDKContext = await this.getMetaOSAppContext();
        const userClickTime = metaHubsSDKContext.app.userClickTime;
        const isBackgroundLoad = metaHubsSDKContext.page.isBackgroundLoad;
        // Mark the SDK initialization
        this.performanceMarker.mark(MarkerTag.InitializedTeamsSDK, {
            isBackgroundLoad: String(isBackgroundLoad),
            userClickTime: String(userClickTime)
        });
        await this.initFromMetaOSAppContext();
    }
    /**
     * Checks if the SDK is initialized
     */ isInitialized() {
        return metaOSAppSDK.app.isInitialized();
    }
    async getInitialRoute() {
        const metaHubsContext = await this.getMetaOSAppContext();
        // We'll try to parse the tab route from PageId, SubPageId and FrameContext
        const tabRoute = tryParseTabRoute(metaHubsContext.page.id, metaHubsContext.page.subPageId, metaHubsContext.page.frameContext, this.loggers);
        if (tabRoute) {
            return tabRoute;
        }
        const newPath = metaHubsContext.page.subPageId;
        return generateRouteFromDeepLink(newPath, this.loggers, generateRouteFromLegacyDeepLink);
    }
    async openLinkInHost(relativeUrl) {
        if (!metaOSAppSDK.app.isInitialized()) {
            this.loggers?.traceLogger.logTrace(0x1e19e809 /* tag_4g46j */ , TraceLevel.Error, `openLinkInHost- Meta Hubs SDK not initialized`);
            throw new Error("Meta Hubs SDK not initialized");
        }
        if (!relativeUrl) {
            this.loggers?.traceLogger.logTrace(0x1e19e808 /* tag_4g46i */ , TraceLevel.Error, `openLinkInHost - Missing relativeUrl`);
            throw new Error("Missing relativeUrl");
        }
        if (!this.isRelativeUrl(relativeUrl)) {
            this.loggers?.traceLogger.logTrace(0x1e19e807 /* tag_4g46h */ , TraceLevel.Error, `openLinkInHost - Invalid URL must be relative`);
            throw new Error("Invalid URL must be relative");
        }
        try {
            const deepLink = new URL(relativeUrl, `${this.rootUrl}`);
            await metaOSAppSDK.app.openLink(deepLink.toString());
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e19e806 /* tag_4g46g */ , TraceLevel.Error, `openLinkInHost - ${generateLogStringFromError(e)}`);
            throw new Error(generateLogStringFromError(e));
        }
    }
    async openExternalAppLinkInHost(externalUrl) {
        if (!metaOSAppSDK.app.isInitialized()) {
            this.loggers?.traceLogger.logTrace(0x1e19e805 /* tag_4g46f */ , TraceLevel.Error, `openExternalAppLinkInHost- Meta Hubs SDK not initialized`);
            throw new Error("Meta Hubs SDK not initialized");
        }
        if (!externalUrl) {
            this.loggers?.traceLogger.logTrace(0x1e19e804 /* tag_4g46e */ , TraceLevel.Error, `openExternalAppLinkInHost - Missing externalUrl`);
            throw new Error("Missing externalUrl");
        }
        try {
            await metaOSAppSDK.app.openLink(externalUrl);
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e19e803 /* tag_4g46d */ , TraceLevel.Error, `openExternalAppLinkInHost - ${generateLogStringFromError(e)}`);
            throw new Error(generateLogStringFromError(e));
        }
    }
    openFilePreviewInHost(fileUrl, fileAlias, route, sharePointBaseUrl, sharePointListItemUniqueId) {
        try {
            const fileExtension = getFileExtension(fileUrl, fileAlias);
            if (fileExtension == null) {
                // Couldn't parse the file type. Fall back to default behavior
                safelyOpenExternalUrl(fileUrl, this.loggers?.traceLogger);
                return;
            }
            if (!sharePointBaseUrl || !sharePointListItemUniqueId) {
                // openFilePreview currently only supports ODSP content and hence requires SP base URL and list item ID
                // IcM: https://portal.microsofticm.com/imp/v5/incidents/details/717605519/summary
                safelyOpenExternalUrl(fileUrl, this.loggers?.traceLogger);
                return;
            }
            // Build metadata
            let decodedUrl = decodeURIComponent(fileUrl);
            if (decodedUrl.endsWith("?web=1")) {
                // Remove the query string `?web=1` from the URL
                decodedUrl = decodedUrl.substring(0, decodedUrl.indexOf("?web=1"));
            }
            let subEntityId = undefined;
            if (route != null) {
                subEntityId = generateDeepLinkFromRoute(route, this.loggers) ?? undefined;
            }
            // Call open in hub preview
            metaOSAppSDK.openFilePreview({
                title: fileAlias,
                type: fileExtension,
                objectUrl: decodedUrl,
                subEntityId: subEntityId,
                entityId: sharePointListItemUniqueId,
                baseUrl: sharePointBaseUrl
            });
        } catch (error) {
            this.loggers?.traceLogger.logTrace(0x1e19e802 /* tag_4g46c */ , TraceLevel.Warning, `Meta Hubs open file preview - Failed ${generateLogStringFromError(error)}`);
            // Fallback to the regular behavior
            safelyOpenExternalUrl(fileUrl, this.loggers?.traceLogger);
        }
    }
    async getAccessTokenForScopes(scopes, claims, silent) {
        if (this.useNestAppAuth && this.configProvider().flights.EnableNestedAppAuth) {
            try {
                return await this.nestedAppAuthHelper.getAccessToken(scopes, claims, silent);
            } catch (error) {
                this.loggers?.traceLogger.logTrace(0x1e08f219 /* tag_4cpiz */ , TraceLevel.Warning, `Error fetching the access token for scopes using NAA: [ErrorMessage=${ErrorUtilities.getMessage(error)}]`);
            }
        } else {
            /**
             * Accesstoken with Scopes is a V2 api and NAA is the only supported way to get token with scopes.
             * Returns error if NAA is not setup.
             */ this.loggers?.traceLogger.logTrace(0x1e08f218 /* tag_4cpiy */ , TraceLevel.Warning, `Error fetching the access token for scopes because NAA is not enabled or not setup successfully.`);
        }
        return Promise.reject(new Error("Failed to get access token for scopes - either NAA is not enabled or not setup successfully or msal failed."));
    }
    // Can be deprecated and above method used if we do not need to support NAA backup, flight graduated.
    async getAccessToken(resource, claims, silent) {
        if (this.useNestAppAuth && this.configProvider().flights.EnableNestedAppAuth) {
            try {
                return await this.nestedAppAuthHelper.getAccessToken([
                    `${resource}/.default`
                ], claims, silent);
            } catch (error) {
                this.loggers?.traceLogger.logTrace(0x1e19e801 /* tag_4g46b */ , TraceLevel.Warning, `Error fetching the access token for resource using NAA: [ErrorMessage=${ErrorUtilities.getMessage(error)}]`);
            }
        }
        // If we fail to get the access token using NAA, we should fall back to getting token using team sdk
        let authTokenRequestParams = {
            resources: [
                resource
            ]
        };
        if (claims) {
            authTokenRequestParams = {
                ...authTokenRequestParams,
                claims: [
                    claims
                ]
            };
        }
        if (silent) {
            authTokenRequestParams = {
                ...authTokenRequestParams,
                silent: true
            };
        }
        let token;
        try {
            token = await metaOSAppSDK.authentication.getAuthToken(authTokenRequestParams);
        } catch (authError) {
            this.loggers?.traceLogger.logTrace(0x1e04e291 /* tag_4bokr */ , TraceLevel.Error, `getAccessToken - Teams SDK getAuthToken failed [Resource=${resource}][HasClaims=${!!claims}][IsSilent=${!!silent}]`);
            throw authError;
        }
        let parsedTokenInfo = null;
        try {
            parsedTokenInfo = JwtDecode(token, {
                header: false
            });
        } catch (err) {
            const traceLevel = this.configProvider().flights.EnableTokenEncryption ? TraceLevel.Warning : TraceLevel.Error;
            this.loggers?.traceLogger.logTrace(0x1e19e800 /* tag_4g46a */ , traceLevel, `getAccessToken - Unable to jwt parse access token.[Resource=${resource}][Claims=${claims}]`);
        }
        const accessToken = {
            accessToken: token,
            expiry: parsedTokenInfo?.exp ? new Date(parsedTokenInfo.exp * 1000) : null
        };
        return accessToken;
    }
    async getMetaOSAppContext() {
        return await this.getContextFromMetaOSAppSDK();
    }
    registerThemeChangeHandler(themeChangeHandler) {
        metaOSAppSDK.app.registerOnThemeChangeHandler((theme)=>{
            themeChangeHandler(this.mapToUnoTheme(theme));
        });
    }
    notifyLoaded() {
        this.loggers?.traceLogger.logTrace(0x1e19e7e3 /* tag_4g459 */ , TraceLevel.Info, `Notify Meta Hubs app loaded.`);
        return metaOSAppSDK.app.notifyAppLoaded();
    }
    registerOnSaveHandler(handler) {
        metaOSAppSDK.pages.config.registerOnSaveHandler(handler);
    }
    registerOnRemoveHandler(handler) {
        metaOSAppSDK.pages.config.registerOnRemoveHandler(handler);
    }
    canSetDocumentTitle() {
        return false;
    }
    /**
     * Collect system performance metrics
     */ collectSystemPerformanceMetrics() {
        return collectScriptCacheMetrics();
    }
    usingNestedAppAuth() {
        return this.useNestAppAuth;
    }
    setValidityState(isValid) {
        metaOSAppSDK.pages.config.setValidityState(isValid);
    }
    notifyRenderSuccess() {
        this.loggers?.traceLogger.logTrace(0x1e19e7e2 /* tag_4g458 */ , TraceLevel.Info, `Notify Meta Hubs render success.`);
        metaOSAppSDK.app.notifySuccess();
        setPageTiming(PageTimingKey.eupl, performance.now());
    }
    notifyRenderFailure(failure) {
        this.loggers?.traceLogger.logTrace(0x1e19e7e1 /* tag_4g457 */ , TraceLevel.Error, `Notify Meta Hubs render failure. [Reason=${failure.reason}]`);
        return metaOSAppSDK.app.notifyFailure(failure);
    }
    notifyExpectedFailure(failure) {
        this.loggers?.traceLogger.logTrace(0x1e19e7e0 /* tag_4g456 */ , TraceLevel.Error, `Notify Meta Hubs expected failure. [Reason=${failure.reason}]`);
        return metaOSAppSDK.app.notifyExpectedFailure(failure);
    }
    static resizeDialog(height, width) {
        metaOSAppSDK.dialog.update.resize({
            height,
            width
        });
    }
    static dismissDialog() {
        metaOSAppSDK.dialog.url.submit();
    }
    /** Authenticate using Meta Hubs sdk's authenticate API
     * @param url - The URL to authenticate against
     * @param onSuccess - The callback to invoke on successful authentication
     * @param onFailure - The callback to invoke on failed authentication
     */ authenticate(url, onSuccess, onFailure) {
        metaOSAppSDK.authentication.authenticate({
            url: url,
            width: 600,
            height: 600,
            successCallback: onSuccess,
            failureCallback: onFailure
        });
    }
    /**
     * Sets the configProvider for the adaptor
     */ setConfigProvider(configProvider) {
        this.configProvider = configProvider;
        this.homeRingId = this.configProvider().sessionMetaData?.homeRingId;
        if (this.normalizeRingId(this.homeRingId) == null) {
            this.loggers?.traceLogger.logTrace(0x1e19e7df /* tag_4g455 */ , TraceLevel.Warning, `setConfigProvider - Invalid session homeRingId. [HomeRingId=${this.homeRingId}]`);
        }
    }
    /**
     * Ensures the ringId value is one of accepted/valid values.
     * Since this is generic implementation, we just ensure it's non-empty string.
     *
     * @remarks
     * Host-specific adaptor should override it and provide host-specific ring validation.
     * @param ringId
     */ normalizeRingId(ringId) {
        if (typeof ringId !== "string" || ringId.trim().length === 0) {
            return null;
        }
        return ringId.trim().toLowerCase();
    }
    /**
     * Returns the root URL of the host app. Hosts overrides this method to return host root url.
     */ computeRootUrl() {
        return window.location.origin;
    }
    /**
     * Maps the Hubs theme to PlannerTheme
     *
     * @remarks
     * Host-specific adaptors should extend this class and override this method if host is providing different theme values.
     *
     * @param theme The Meta Hubs theme
     */ mapToUnoTheme(theme) {
        switch(theme.trim().toLowerCase()){
            case "dark":
                return PlannerTheme.Dark;
            case "contrast":
                return PlannerTheme.HighContrast;
            case "default":
            case "light":
            default:
                return PlannerTheme.Light;
        }
    }
    /**
    /* Checks if the ringId is present in the Meta Hubs context
    * @returns true if ringId is present, false otherwise
    */ isRingIdPresent() {
        return this.ringId != null;
    }
    /**
     * Returns the context from the Meta Hubs SDK
     */ async getContextFromMetaOSAppSDK() {
        if (!metaOSAppSDK.app.isInitialized()) {
            this.loggers?.traceLogger.logTrace(0x1e19e7de /* tag_4g454 */ , TraceLevel.Error, `Meta Hubs SDK failed to initialize.`);
        }
        const context = await metaOSAppSDK.app.getContext();
        return context;
    }
    /**
     * Returns the appropriate TelemetryPlatform based on the Meta Hubs HostClientType
     */ getTelemetryPlatform(hostClientType) {
        switch(hostClientType){
            case HostClientType.desktop:
                return TelemetryPlatform.Desktop;
            case HostClientType.web:
                return TelemetryPlatform.Web;
            case HostClientType.android:
                return TelemetryPlatform.Android;
            case HostClientType.ios:
            case HostClientType.ipados:
                return TelemetryPlatform.iOS;
            case HostClientType.macos:
                return TelemetryPlatform.macOS;
            case HostClientType.surfaceHub:
            case HostClientType.teamsDisplays:
            case HostClientType.teamsPhones:
            case HostClientType.teamsRoomsAndroid:
            case HostClientType.teamsRoomsWindows:
            case HostClientType.rigel:
            case HostClientType.visionOS:
                return TelemetryPlatform.Other;
            default:
                ErrorUtilities.unreachableCase(hostClientType, (log)=>{});
                return TelemetryPlatform.Other;
        }
    }
    /**
     * Checks if the URL is a relative URL
     * @param url The URL to check
     * @returns true if the URL is relative, false otherwise
     */ isRelativeUrl(url) {
        const lowerCaseRelativeUrl = url.toLowerCase();
        return !(lowerCaseRelativeUrl.startsWith("http") || lowerCaseRelativeUrl.startsWith("//"));
    }
    /**
     * Constructor for MetaOSHostAdaptor
     * @param performanceMarker - The performance marker. It is needed as the loggers will not be available upfront.
     * @param appName - The name of the application to be displayed in telemetry
     */ constructor(performanceMarker, appName){
        this.performanceMarker = performanceMarker;
        this.loggers = null;
        // The unique ID defined for this app page - Typically it'll be "mytasks" for PROD
        this.pageId = null;
        this.appId = null;
        this.tenantId = null;
        this.ringId = null;
        this.homeRingId = null;
        this.useNestAppAuth = false;
        this._rootUrl = undefined;
        this.appName = appName;
    }
}
